<template>
  <div>
    <div class="c-tabs-secondary">
      <div class="c-tabs-secondary-header">
        <template v-if="!isEditing">
          <span
            v-for="(tab, index) in tabsSecondaryPlaceholderData"
            :key="`tab${index}`"
            :class="`tab-nav-item ${index === activeTabIndex ? 'active' : ''}`"
          >
            <button class="tab-nav-link" v-on:click="activeTabIndex = index">
              <sc-text :field="tab.$props.fields.name" />
            </button>
          </span>
        </template>
      </div>
      <div :class="{ 'c-tabs-secondary-content': !isEditing }">
        <template v-if="isEditing">
          <component
            v-for="(tab, index) in tabsSecondaryPlaceholderData"
            :is="tab"
            :key="`tab${index}`"
          />
        </template>
        <template v-else>
          <component :is="activeTab" />
        </template>
      </div>
    </div>

    <!-- Mobile - Tab Accordion -->
    <div class="c-tabs-secondary-mobile">
      <template v-if="!isEditing">
        <div
          v-for="(tab, index) in tabsSecondaryPlaceholderData"
          :key="`acc${index}`"
          :class="{ active: isOpen.includes(index) }"
        >
          <div class="c-tabs-secondary-mobile__title" @click="toggle(index)">
            <sc-text :field="tab.$props.fields.name" />
          </div>
          <div class="c-tabs-secondary-mobile__content">
            <component :is="tab" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { Text, File } from '@sitecore-jss/sitecore-jss-vue';
import findParentComponentByName from '../../lib/findParentComponentByName';

export default {
  name: 'CIS-Tabs-Secondary',
  components: {
    ScText: Text,
    ScFile: File
  },
  props: {
    fields: {
      type: Object
    },
    rendering: {
      type: Object
    }
  },
  data() {
    return {
      activeTabIndex: 0,
      isOpen: [],
      tabsSecondaryPlaceholderData: markRaw(this.$options.computed.tabsSecondaryPlaceholder)
    };
  },
  withPlaceholder() {
    return {
      placeholders: [
        {
          placeholder: 'jss-tabs-secondary',
          computedPropName: 'tabsSecondaryPlaceholder'
        }
      ]
    };
  },
  updated() {
    findParentComponentByName(this, 'Layout')?.externalLinks();
  },
  computed: {
    isEditing() {
      return this.$jss.sitecoreContext().pageEditing;
    },
    activeTab() {
      return (
        this.tabsSecondaryPlaceholderData != null &&
        this.tabsSecondaryPlaceholderData[this.activeTabIndex]
      );
    }
  },
  methods: {
    toggle(index) {
      if (this.isOpen.includes(index)) {
        this.isOpen = this.isOpen.filter((i) => i !== index);
        return;
      }

      this.isOpen.push(index);
    }
  }
};
</script>

<style lang="scss" scoped>
.c-tabs-secondary {
  display: none;

  @include tablet-up {
    display: block;
  }

  &-header {
    display: flex;
    border-bottom: solid 0.2rem $divider;

    .tab-nav-item {
      display: flex;
      justify-content: center;
      margin-right: 2rem;
      padding: 1rem 2rem;
      @include font-styles('Arial Narrow', 1.8rem, 600, normal, 2.2rem, 0, $gray);

      @include tablet-up {
        font-size: 1.6rem;
      }

      @include large-desktop-up {
        font-size: 1.8rem;
      }

      &.active {
        position: relative;
        color: $blue;

        &:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -0.3rem;
          width: 100%;
          height: 0.4rem;
          background-color: $blue;
        }
      }

      &:last-child {
        margin-right: 0;
      }

      span {
        text-transform: uppercase;
      }
    }
    .tab-nav-link {
      border: none;
      background: none;
    }
  }

  &-content {
    display: flex;
    padding-top: 2rem;

    @include desktop-up {
      padding-top: 3.5rem;
    }
  }

  &-mobile {
    @include tablet-up {
      display: none;
    }

    > div:last-child {
      .c-tabs-secondary-mobile__title {
        border: none;
      }
    }

    &__title {
      position: relative;
      padding: 2.2rem 0;
      margin-bottom: 0.4rem;
      border-bottom: solid 0.1rem $divider;
      @include font-styles('Arial', 1.8rem, 300, normal, 2.4rem, 0, $blue);

      @include tablet-up {
        padding: 2.2rem 5.5rem 2.2rem 2rem;
      }

      &:before {
        position: absolute;
        font-weight: 700;
        font-size: 2rem;
        content: '∟';
        transform: translate(0, -70%) rotate(-45deg);
        color: $blue;
        right: 0;
        top: 50%;
        pointer-events: none;
        transition: all 0.5s;

        @include tablet-up {
          right: 2rem;
        }
      }

      span {
        text-transform: inherit;
      }
    }

    &__content {
      display: none;
      padding-top: 2rem;

      @include tablet-up {
        padding: 2rem;
      }
    }

    .active {
      .c-tabs-secondary-mobile__title {
        border-bottom: solid 0.1rem $divider !important;

        &:before {
          transform: translate(0, -50%) rotate(135deg);
        }
      }

      .c-tabs-secondary-mobile__content {
        display: block;
      }
    }
  }
}
</style>
